/* eslint-disable */
import type { ColorFilterConfig } from '../core/types'

/**
 * Custom transformer for the `@vonage/media-processor` pipeline.
 *
 * Applies color adjustments (brightness, contrast, saturation, sepia,
 * grayscale) via OffscreenCanvas CSS filters, and temperature via a
 * canvas color overlay.
 *
 * Implements the `Transformer` interface expected by
 * `MediaProcessor.setTransformers()`.
 *
 * Performance: each frame costs < 2 ms on mid-range hardware thanks to
 * GPU-accelerated `CanvasRenderingContext2D.filter`.
 */
export class ColorFilterTransformer {
  private config: Required<ColorFilterConfig>
  private canvas: OffscreenCanvas | null = null
  private ctx: OffscreenCanvasRenderingContext2D | null = null

  /**
   * @param config - Initial color values. All fields are optional;
   *                 omitted values default to neutral (identity).
   */
  constructor (config: ColorFilterConfig = {}) {
    this.config = ColorFilterTransformer.normalize(config)
  }

  /**
   * Hot-update the color values without recreating the transformer.
   *
   * @param config - Partial or full color configuration.
   */
  updateConfig (config: ColorFilterConfig): void {
    this.config = ColorFilterTransformer.normalize(config)
  }

  /**
   * Return a defensive copy of the current normalized configuration.
   */
  getConfig (): Required<ColorFilterConfig> {
    return { ...this.config }
  }

  /**
   * Called by `MediaProcessor` for every video frame in the pipeline.
   *
   * When all values are at their identity (neutral) positions the frame
   * is enqueued untouched, avoiding any canvas work.
   *
   * @param frame      - Incoming `VideoFrame` from the pipeline.
   * @param controller - `TransformStream` controller to enqueue processed frames.
   */
  async transform (frame: VideoFrame, controller: TransformStreamDefaultController): Promise<void> {
    if (ColorFilterTransformer.isIdentity(this.config)) {
      controller.enqueue(frame)
      return
    }

    const width = frame.displayWidth
    const height = frame.displayHeight

    if (!this.canvas || this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas = new OffscreenCanvas(width, height)
      this.ctx = this.canvas.getContext('2d') as OffscreenCanvasRenderingContext2D
    }

    if (!this.ctx) {
      controller.enqueue(frame)
      return
    }

    let processed: VideoFrame | null = null
    try {
      const ctx = this.ctx

      ctx.filter = ColorFilterTransformer.buildCSSFilterString(this.config)
      ctx.drawImage(frame as any, 0, 0, width, height)

      if (this.config.temperature !== 0) {
        this.applyTemperature(ctx, width, height)
      }

      processed = new VideoFrame(this.canvas as any, {
        timestamp: frame.timestamp || 0
      })

      controller.enqueue(processed)
    } catch (e) {
      if (processed) {
        processed.close()
      }
      controller.enqueue(frame)
      return
    }
    frame.close()
  }

  /** Release the internal canvas and context. */
  destroy (): void {
    this.canvas = null
    this.ctx = null
  }

  /**
   * Temperature is not a native CSS filter. We approximate it with a
   * semi-transparent color overlay using 'color' composite mode.
   * Positive = warm (amber), negative = cool (blue).
   */
  private applyTemperature (ctx: OffscreenCanvasRenderingContext2D, w: number, h: number): void {
    const intensity = Math.min(Math.abs(this.config.temperature), 1)
    const alpha = intensity * 0.15

    ctx.save()
    ctx.globalCompositeOperation = 'color'
    ctx.fillStyle = this.config.temperature > 0
      ? `rgba(255, 147, 41, ${alpha})`
      : `rgba(41, 121, 255, ${alpha})`
    ctx.fillRect(0, 0, w, h)
    ctx.restore()
  }

  /**
   * Fill in missing values with identity (no-change) defaults.
   *
   * @param config - Partial color config from the consumer.
   * @returns Fully-populated config.
   */
  static normalize (config: ColorFilterConfig): Required<ColorFilterConfig> {
    return {
      brightness: config.brightness ?? 1,
      contrast: config.contrast ?? 1,
      saturation: config.saturation ?? 1,
      sepia: config.sepia ?? 0,
      grayscale: config.grayscale ?? 0,
      temperature: config.temperature ?? 0
    }
  }

  /**
   * Check whether the config represents identity (no visual change).
   * Used to skip canvas work entirely when no adjustment is needed.
   */
  static isIdentity (config: ColorFilterConfig): boolean {
    const c = ColorFilterTransformer.normalize(config)
    return c.brightness === 1 &&
      c.contrast === 1 &&
      c.saturation === 1 &&
      c.sepia === 0 &&
      c.grayscale === 0 &&
      c.temperature === 0
  }

  /**
   * Build a CSS `filter` string from the config values.
   *
   * @returns A space-separated filter function string, or `'none'`.
   */
  static buildCSSFilterString (config: ColorFilterConfig): string {
    const c = ColorFilterTransformer.normalize(config)
    const parts: string[] = []

    if (c.brightness !== 1) parts.push(`brightness(${c.brightness})`)
    if (c.contrast !== 1) parts.push(`contrast(${c.contrast})`)
    if (c.saturation !== 1) parts.push(`saturate(${c.saturation})`)
    if (c.sepia > 0) parts.push(`sepia(${c.sepia})`)
    if (c.grayscale > 0) parts.push(`grayscale(${c.grayscale})`)

    return parts.length > 0 ? parts.join(' ') : 'none'
  }
}
